import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { EmployeeReminderTable } from '@app/modules/employees/services/tables/reminders/employee-reminder.table';
import { ReminderForm } from '@app/modules/forms/employees/components';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { IndexView } from '@common/views';
import { BaseForm } from '@forms/base.form';
import { Employee } from '@models/employee/employee.model';
import { Reminder } from '@models/employee/reminder.model';

@Component({
    templateUrl: './reminders-index.template.html',
    styleUrls: ['./reminders-index.style.scss'],
})
export class RemindersIndexView extends IndexView {
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('editModal', { static: true }) editModal: DeprecatedModalFormComponent;
    @ViewChild('editForm', { static: true }) editForm: ReminderForm;

    isSelfEmployeeView = false;

    createReminderPermission: string[] = Reminder.permission.edit;
    isLoading = false;

    employee: Employee;
    selectedReminder: Reminder;
    table: EmployeeReminderTable;

    constructor(
        protected element: ElementRef,
        protected route: ActivatedRoute,
        protected auth: AuthService,
        protected notify: NotifyService,
        protected analyticsService: AnalyticService
    ) {
        super();
        this.employee = this.route.snapshot.data.models.get('employee');
        this.table = new EmployeeReminderTable(this.auth, this.employee);
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const employmentReminderData = data as Reminder;
        switch (menuItem.event) {
            case 'delete':
                this.onDelete(employmentReminderData);
                break;
            case 'edit':
                this.editForm.setReminder(employmentReminderData);
                this.editModal.show();
                break;
        }
    }

    onDelete(reminder: Reminder): void {
        this.confirmDeleteDialog.show().then((yes: boolean) => {
            if (yes) {
                this.isLoading = true;
                reminder
                    .param('company', this.auth.company.id)
                    .param('employee', this.employee.id)
                    .delete()
                    .then(() => {
                        this.isSelfEmployeeView
                            ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileRemindersTabDeleteReminder)
                            : this.analyticsService.trackEvent(
                                  AnalyticEvents.EmployeeProfileRemindersTabDeleteReminder
                              );

                        this.notify.success('deleted');
                        this.isLoading = false;
                        this.table.reload();
                    })
                    .catch((err) => {
                        this.notify.error(err.message || 'account.deleteError');
                        this.isLoading = false;
                    });
            }
        });
    }

    onSelect(reminder: Reminder): void {
        this.selectedReminder = reminder;
    }

    onCreate(reminder: Reminder, form?: BaseForm): void {
        if (!reminder.isDirty()) {
            return;
        }

        const isExistingReminder = Boolean(reminder.id);

        this.isLoading = true;
        reminder
            .param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .save()
            .then(() => {
                if (form) {
                    form.reset();
                }

                // Add Pendo Tracking

                if (isExistingReminder) {
                    this.isSelfEmployeeView
                        ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileRemindersTabEditReminder)
                        : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileRemindersTabEditReminder);
                } else {
                    this.isSelfEmployeeView
                        ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileRemindersTabCreateReminder)
                        : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileRemindersTabCreateReminder);
                }

                this.notify.success('account.saved');
                this.isLoading = false;
                this.table.reload();
            })
            .catch((err) => {
                this.notify.error(err.message || 'account.saveError');
                this.isLoading = false;
            });
    }
}
